import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";
import api from "../../../api/axios";
import { downloadCSV, downloadExcel } from "./reportsUtility";
import ReportPageLayout from "./ReportsPage";

/**
 * Unified Tab-based Reports Page (Option A style)
 * Tabs: Summary | PF | PT | ESIC | TDS | LOP | Bank | Payslips | Consolidated
 *
 * - Uses endpoints: /hrms/prms/reports/{report}/{period}
 * - Consolidated download uses /hrms/prms/reports/consolidated/{period}
 * - Payslip view uses /hrms/prms/runs/{runId} (if you want run-based), here we fetch payslips endpoint by period
 */

const TABS = [
  { id: "summary", label: "Summary", api: "summary" },
  { id: "pf", label: "PF", api: "pf" },
  { id: "pt", label: "PT", api: "pt" },
  { id: "esi", label: "ESIC", api: "esi" },
  { id: "tds", label: "TDS", api: "tds" },
  { id: "lop", label: "LOP", api: "lop" },
  { id: "bank", label: "Bank", api: "bank" },
  { id: "payslips", label: "Payslips", api: "payslips" },
];

export default function ReportsTabs() {
  const [period, setPeriod] = useState(() => {
    const now = new Date();
    const mm = String(now.getMonth() + 1).padStart(2, "0");
    return `${now.getFullYear()}-${mm}`;
  });

  const [tab, setTab] = useState("summary");
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [usingSample, setUsingSample] = useState(false);

  useEffect(() => {
    // load default tab when period or tab changes
    fetchTab();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, period]);

  const formatPeriodLabel = (p) => {
    if (!p) return "";
    try {
      const [y, m] = p.split("-");
      const date = new Date(`${y}-${m}-01`);
      return date.toLocaleString("en-IN", { month: "long", year: "numeric" });
    } catch {
      return p;
    }
  };

  const fetchTab = async () => {
    if (!period) {
      setRows([]);
      return;
    }
    setLoading(true);
    setUsingSample(false);
    setRows([]);
    try {
      // special case: consolidated uses different request -> not here
      const apiPath = `hrms/prms/reports/${
        TABS.find((t) => t.id === tab)?.api
      }/${period}`;
      const res = await api.get(apiPath);
      const d = res?.data?.data.rows ?? [];
      setRows(d);
    } catch (err) {
      console.warn("report fetch failed", err);
      // fallback sample data for summary/tab
      setUsingSample(true);
      setRows(sampleDataFor(tab));
    }
    setLoading(false);
  };

  const sampleDataFor = (tabId) => {
    switch (tabId) {
      case "summary":
        return [
          {
            name: "Alice Johnson",
            gross: 95833,
            deductions: 11700,
            net: 84133,
          },
          { name: "Bob Williams", gross: 58333, deductions: 7200, net: 51133 },
        ];
      case "pf":
        return [
          {
            employee_code: "EMP001",
            name: "Alice",
            basic: 50000,
            employee_pf: 6000,
            employer_pf: 6000,
            pension: 4165,
          },
        ];
      case "pt":
        return [{ employee_code: "EMP002", name: "Bob", pt_amount: 200 }];
      case "esi":
        return [
          {
            employee_code: "EMP003",
            name: "Charlie",
            gross: 125000,
            esi_employee: 937.5,
            esi_employer: 4062.5,
          },
        ];
      case "tds":
        return [
          {
            employee_code: "EMP001",
            name: "Alice",
            taxable_income: 75000,
            tds: 2500,
          },
        ];
      case "lop":
        return [
          {
            employee_code: "EMP002",
            name: "Bob",
            working_days: 30,
            present: 28,
            leave: 0,
            absent: 2,
            lop_amount: 2000,
          },
        ];
      case "bank":
        return [
          {
            employee_code: "EMP003",
            name: "Charlie",
            bank_account: "1234567890",
            ifsc: "HDFC0000001",
            net_salary: 50000,
          },
        ];
      case "payslips":
        return [
          {
            employee_id: 1,
            employee: { first_name: "Alice", last_name: "J" },
            gross: 95833,
            deductions: 11700,
            net: 84133,
            meta: {},
          },
        ];
      default:
        return [];
    }
  };

  /* Download helpers */
  const handleDownloadCSV = () => {
    if (!rows?.length)
      return Swal.fire("No data", "Generate the report first", "info");
    downloadCSV(rows, `${tab}_${period}`);
  };

  const handleDownloadExcel = () => {
    if (!rows?.length)
      return Swal.fire("No data", "Generate the report first", "info");
    downloadExcel(rows, `${tab}_${period}`);
  };

  const handleDownloadConsolidated = async () => {
    if (!period) return Swal.fire("Select period", "", "warning");
    try {
      const res = await api.get(`hrms/prms/reports/consolidated/${period}`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `reports_${period}.zip`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      Swal.fire("Error", "Failed to download consolidated report", "error");
    }
  };

  /* Payslip download single item (render current selected first row) */
  const downloadPayslipPdf = async (item) => {
    if (!item) return;
    try {
      const response = await api
        .get(
          `/hrms/prms/reports/download?type=payslips&period=${period}&employee_id=${item.employee_id}`,
          {
            responseType: "blob",
          }
        )
        .then((response) => {
          // 1️⃣ Get filename from header
          const disposition = response.headers["content-disposition"];
          let filename = "download.pdf";

          if (disposition && disposition.includes("filename=")) {
            filename = disposition
              .split("filename=")[1]
              .replace(/"/g, "")
              .trim();
          }

          // 2️⃣ Create download link
          const fileURL = window.URL.createObjectURL(new Blob([response.data]));

          const link = document.createElement("a");
          link.href = fileURL;
          link.setAttribute("download", filename);
          document.body.appendChild(link);
          link.click();
          link.remove();
        });
    } catch (err) {
      Swal.fire("Error", "Unable to download report.", "error");
    }
    // render a minimal HTML and pass to html2pdf
    // const el = document.createElement("div");
    // el.innerHTML = payslipHtml(item, period);
    // html2pdf()
    //   .from(el)
    //   .save(
    //     `payslip_${
    //       item.employee?.employee_code || item.employee_id || "emp"
    //     }.pdf`
    //   );
  };

  /* Small HTML generator - kept minimal so pdf looks neat */
  const payslipHtml = (item, p) => {
    const emp = item.employee || {};
    const comps = item.meta?.components || [];
    const earnings = comps.filter((c) => c.type === "earning");
    const deductions = comps.filter((c) => c.type === "deduction");

    const monthLabel = formatPeriodLabel(p);
    return `
      <div style="font-family: Arial, Helvetica, sans-serif; padding:24px; max-width:760px;">
        <h2 style="text-align:center; margin-bottom:4px">Payslip</h2>
        <div style="text-align:center; color:#666; margin-bottom:18px">Salary for ${monthLabel}</div>
        <hr/>
        <h4>Employee Details</h4>
        <div>Name: ${emp.first_name || ""} ${emp.last_name || ""}</div>
        <div>Code: ${emp.employee_code || ""}</div>
        <hr/>
        <h4>Earnings</h4>
        <table style="width:100%; border-collapse:collapse">
          ${earnings
            .map(
              (e) =>
                `<tr><td>${
                  e.component
                }</td><td style="text-align:right">₹${Number(
                  e.amount || 0
                ).toFixed(2)}</td></tr>`
            )
            .join("")}
          <tr style="font-weight:bold"><td>Gross</td><td style="text-align:right">₹${Number(
            item.gross || 0
          ).toFixed(2)}</td></tr>
        </table>
        <h4 style="margin-top:14px">Deductions</h4>
        <table style="width:100%; border-collapse:collapse">
          ${deductions
            .map(
              (e) =>
                `<tr><td>${
                  e.component
                }</td><td style="text-align:right">₹${Number(
                  e.amount || 0
                ).toFixed(2)}</td></tr>`
            )
            .join("")}
          <tr style="font-weight:bold"><td>Total</td><td style="text-align:right">₹${Number(
            item.deductions || 0
          ).toFixed(2)}</td></tr>
        </table>
        <hr/>
        <h3 style="text-align:right">Net Salary: ₹${Number(
          item.net || 0
        ).toFixed(2)}</h3>
      </div>
    `;
  };

  const downloadReport = async (type) => {
    if (!period) {
      Swal.fire("Missing Month", "Please select a month", "warning");
      return;
    }

    try {
      const response = await api
        .get(`/hrms/prms/reports/download?type=${type}&period=${period}`, {
          responseType: "blob",
        })
        .then((response) => {
          const disposition = response.headers["content-disposition"];
          let filename = "download.pdf";

          if (disposition && disposition.includes("filename=")) {
            filename = disposition
              .split("filename=")[1]
              .replace(/"/g, "")
              .trim();
          }

          const fileURL = window.URL.createObjectURL(new Blob([response.data]));

          const link = document.createElement("a");
          link.href = fileURL;
          link.setAttribute("download", filename);
          document.body.appendChild(link);
          link.click();
          link.remove();
        });
    } catch (err) {
      Swal.fire("Error", "Unable to download report.", "error");
    }
  };

  /* Render table body based on tab */
  const renderTable = () => {
    if (loading) return <div className="text-center p-4">Loading...</div>;
    if (!rows || !rows.length)
      return (
        <div className="text-center text-muted p-4">
          No data — generate report
        </div>
      );

    switch (tab) {
      case "summary":
        return (
          <table className="table">
            <thead>
              <tr>
                <th>Employee</th>
                <th>Gross</th>
                <th>Deductions</th>
                <th>Net</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i}>
                  <td>{r.employee || r.name}</td>
                  <td>
                    ₹
                    {Number(r.gross || r.gross || 0).toLocaleString("en-IN", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </td>
                  <td>
                    ₹
                    {Number(r.deductions || 0).toLocaleString("en-IN", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </td>
                  <td>
                    ₹
                    {Number(r.net || 0).toLocaleString("en-IN", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        );
      case "pf":
        return (
          <table className="table">
            <thead>
              <tr>
                <th>Employee</th>
                <th>Basic</th>
                <th>Employee PF</th>
                <th>Employer PF</th>
                <th>Pension</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i}>
                  <td>
                    {r.employee_code} - {r.name}
                  </td>
                  <td>₹{Number(r.basic || 0).toFixed(2)}</td>
                  <td>₹{Number(r.employee_pf || 0).toFixed(2)}</td>
                  <td>₹{Number(r.employer_pf || 0).toFixed(2)}</td>
                  <td>₹{Number(r.pension || 0).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        );
      case "pt":
        return (
          <table className="table">
            <thead>
              <tr>
                <th>Employee</th>
                <th>PT Amount</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i}>
                  <td>
                    {r.employee_code} - {r.name}
                  </td>
                  <td>₹{Number(r.pt_amount || 0).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        );
      case "esi":
        return (
          <table className="table">
            <thead>
              <tr>
                <th>Employee</th>
                <th>Gross</th>
                <th>ESI Employee</th>
                <th>ESI Employer</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i}>
                  <td>
                    {r.employee_code} - {r.name}
                  </td>
                  <td>₹{Number(r.gross || 0).toFixed(2)}</td>
                  <td>₹{Number(r.esi_employee || 0).toFixed(2)}</td>
                  <td>₹{Number(r.esi_employer || 0).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        );
      case "tds":
        return (
          <table className="table">
            <thead>
              <tr>
                <th>Employee</th>
                <th>Taxable Income</th>
                <th>TDS</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i}>
                  <td>
                    {r.employee_code} - {r.name}
                  </td>
                  <td>₹{Number(r.taxable_income || 0).toFixed(2)}</td>
                  <td>₹{Number(r.tds || 0).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        );
      case "lop":
        return (
          <table className="table">
            <thead>
              <tr>
                <th>Employee</th>
                <th>Working</th>
                <th>Present</th>
                <th>Leave</th>
                <th>Absent</th>
                <th>LOP Amount</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i}>
                  <td>
                    {r.employee_code} - {r.name}
                  </td>
                  <td>{r.working_days}</td>
                  <td>{r.present}</td>
                  <td>{r.leave}</td>
                  <td>{r.absent}</td>
                  <td>₹{Number(r.lop_amount || 0).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        );
      case "bank":
        return (
          <table className="table">
            <thead>
              <tr>
                <th>Employee</th>
                <th>Account</th>
                <th>IFSC</th>
                <th>Net Salary</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i}>
                  <td>
                    {r.employee_code} - {r.name}
                  </td>
                  <td>{r.bank_account || "—"}</td>
                  <td>{r.ifsc || "—"}</td>
                  <td>₹{Number(r.net_salary || r.net || 0).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        );
      case "payslips":
        return (
          <>
            <div className="mb-2">
              <small className="text-muted">
                Click an employee to download their payslip PDF
              </small>
            </div>
            <div className="list-group">
              {rows.map((r, i) => (
                <div
                  key={i}
                  className="list-group-item d-flex justify-content-between align-items-center"
                >
                  <div>
                    <div className="fw-semibold">{r?.name}</div>
                    <small className="text-muted">{r?.employee_code}</small>
                  </div>
                  <div className="d-flex gap-2">
                    <button
                      className="btn btn-sm btn-outline-primary"
                      onClick={() => downloadPayslipPdf(r)}
                    >
                      Download PDF
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        );
      default:
        return null;
    }
  };

  /* right side quick links UI */
  const RightSide = () => (
    <>
      <h5 className="fw-bold">Available Reports</h5>
      <p className="text-muted small">
        Download individual reports for the month.
      </p>

      {TABS.map((t) => (
        <div
          key={t.id}
          className="d-flex justify-content-between align-items-center py-2 border-bottom"
        >
          <div>
            <i className="bi bi-file-earmark-text me-2"></i>
            <span className="fw-semibold">{t.label} Report</span>
          </div>

          <div>
            {/* Open report endpoint in new tab (API will respond with file if implemented) */}
            <button
              className="btn btn-link p-0"
              onClick={() => downloadReport(t.id)}
            >
              <i className="bi bi-download fs-4"></i>
            </button>
          </div>
        </div>
      ))}

      <div className="mt-3 d-flex flex-column gap-2">
        <button className="btn btn-outline-primary" onClick={handleDownloadCSV}>
          Download CSV
        </button>
        <button
          className="btn btn-outline-success"
          onClick={handleDownloadExcel}
        >
          Download Excel
        </button>
        {/* <button
          className="btn btn-primary"
          onClick={handleDownloadConsolidated}
        >
          Download Consolidated ZIP
        </button> */}
      </div>
    </>
  );

  return (
    <ReportPageLayout
      title="Monthly Payroll Report"
      period={period ? period : "Select month"}
      rightSide={<RightSide />}
    >
      {/* Tabs header */}
      <div className="mb-3 d-flex justify-content-start">
        <div style={{ maxWidth: "200px" }}>
          <input
            type="month"
            className="form-control"
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
          />
        </div>
      </div>
      <div className="mb-3 d-flex align-items-center justify-content-between gap-3">
        <div className="d-flex gap-2">
          {TABS.map((t) => (
            <button
              key={t.id}
              className={`btn ${
                tab === t.id ? "btn-primary" : "btn-outline-secondary"
              }`}
              onClick={() => setTab(t.id)}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="d-flex gap-2 align-items-center">
          <button className="btn btn-outline-primary" onClick={fetchTab}>
            Generate
          </button>
        </div>
      </div>

      {/* Main content (table / list per tab) */}
      <div>{renderTable()}</div>
    </ReportPageLayout>
  );
}
